import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Typography, useMediaQuery, useTheme } from '@mui/material';
import { useSnackbar } from 'notistack';
import clsx from 'clsx';
import AlertDialog from '../../../../components/ui/Dialog/AlertDialog';
import Preloader from '../../../../components/ui/Preloader/Preloader';

import { IAppState } from '../../../../store/rootDuck';
import { actions as categoriesActions } from '../../../../store/ducks/categories.duck';
import { actions as productCatalogActions } from '../../../../store/ducks/product-catalog.duck';
import { actions as profileActions } from '../../../../store/ducks/profile.duck';
import { actions as cartActions } from '../../../../store/ducks/cart.duck';
import { setLayoutFooter, setLayoutSubheader } from '../../../../utils/layout';
import useCrudSnackbar from '../../../../hooks/useCrudSnackbar';
import { IProduct } from '../../../../interfaces/product';
import { ProductsGrid } from '../components';
import ProductsTableListComponent from '../components/ProductTable/ProductsTableList';
import FilterCategoriesTree from '../components/FilterCategoriesTree';
import { useStylesProductCatalog } from './styles';
import { useDefineUserRole, useFormatMessage } from '../../../../hooks';
import ProductsTableListForAdminComponent from '../components/ProductsTableListForAdmin';
import ProductsTableForUsersComponent from '../components/ProductsTableForUsers';
import ProductCatalogHeader from '../components/ProductCatalogHeader/ProductCatalogHeader';
import { useGetProductsCompany } from '../hooks/useGetProductsCompany';
import { CompanyContext } from './CompanyContext';
import CompanyBind from '../../companies/CompanyBind';
import { useFetchCatalogCategories } from '../../../../hooks/useFetchCatalogCategories';

type CategoriesMethods = {
  resetCategories: () => void;
} | null;

const ProductCatalogCompany: React.FC<TPropsFromRedux> = ({
  search,
  sortType,
  deleteError,
  me,
  loadingMe,
  addProductLoading,
  addProductError,
  cartCompanyId,
  isAuthorized,
  companyGuestCart,
  // catalogCategories,
  isAdmin,
  delProduct,
  clearDel,
  fetchMe,
  clearMe,
  addProductToCart,
  setProductGuestCart,
  // fetchCatalogCategories,
  viewType,
  setViewType,
  setSearch,
  isSearching,
  clearAddProduct,
  delSuccess,
  categoryIdActive,
  setCategoryIdActive,
}) => {
  const categoriesRef = useRef<CategoriesMethods>(null);
  const { id } = useParams();
  const companyId = Number(id || 0);
  const { classes } = useStylesProductCatalog();
  const intl = useIntl();
  const fm = useFormatMessage();
  const navigate = useNavigate();
  const locationHistory: any = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { fetchCatalogCategoriesCompany, companyCategories } = useFetchCatalogCategories();
  const [deleteId, setDeleteId] = useState<number | undefined>(-1);
  const [isAlertOpen, setAlertOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(0);
  const { fetch, loading, page, products } = useGetProductsCompany();
  const isManager = useDefineUserRole(me, 'ROLE_MANAGER');
  const isVendor = useDefineUserRole(me, ['ROLE_VENDOR', 'ROLE_VENDOR_STAFF']);

  const effectiveViewType = isMobile && viewType === 'list' ? 'grid' : viewType;

  const clearAllFilters = useCallback(() => {
    localStorage.removeItem('category');
  }, []);

  const handleDeleteDialog = useCallback((item: IProduct) => {
    setDeleteId(item.id);
    setAlertOpen(true);
  }, []);

  useEffect(() => {
    fetchMe();
    return () => {
      clearAllFilters();
      clearMe();
      clearDel();
      clearAddProduct();
    };
  }, []);

  useEffect(() => {
    fetchCatalogCategoriesCompany({ only_active: 1, company_id: +companyId!! });
  }, [me]);

  const deleteAction = useCallback(() => {
    setAlertOpen(false);
    delProduct({
      id: deleteId,
      page: 1,
      perPage: 10,
      categoryId: categoryIdActive || selectedCategoryId || 0,
    });
  }, [deleteId, delProduct]);

  const editAction = useCallback(
    (item: IProduct, isReviews?: boolean) => {
      navigate(`/products/edit/${item.id}`, { state: { isReviews } });
    },
    [navigate]
  );

  const handleCartDialog = useCallback(
    (item: any, count = 1) => {
      setProductGuestCart({ data: item, type: 'cart', count });
    },
    [addProductToCart, cartCompanyId, companyGuestCart, setProductGuestCart]
  );

  useCrudSnackbar({
    success: delSuccess,
    error: deleteError,
    successMessage: intl.formatMessage({ id: 'PRODUCT.DELETED' }),
    errorMessage: `${intl.formatMessage({ id: 'ERROR' })}: ${addProductError}`,
    afterSuccessOrError: () => {
      setAlertOpen(false);
      clearDel();
      fetch({
        id: companyId,
        data: {
          page: 1,
          per_page: 10,
          category_id: categoryIdActive || selectedCategoryId || undefined,
        },
      });
    },
  });
  useEffect(() => {
    setViewType('grid');
  }, []);

  useEffect(() => {
    if (locationHistory?.state?.categoryId) {
      setSearch(true);
      setSelectedCategoryId(locationHistory.state.categoryId);
      setCategoryIdActive(locationHistory.state.categoryId);
    }
    search && setSearch(true);
  }, [sortType, search]);

  useEffect(() => {
    if (deleteError) {
      enqueueSnackbar(deleteError, { variant: 'error' });
      setAlertOpen(false);
      clearDel();
    }
    return () => {
      clearDel();
    };
  }, [deleteError, clearDel, enqueueSnackbar]);

  useEffect(() => {
    fetch({
      id: companyId,
      data: {
        page: 1,
        per_page: 10,
        category_id: categoryIdActive || selectedCategoryId || undefined,
      },
    });
  }, []);

  const findPathById = (arr: any[], targetId: number | undefined | null, path = []): any => {
    if (!arr.length || !targetId) {
      return ['Нет даты'];
    }

    for (const item of arr) {
      const newPath: any = [...path, { name: item.name, id: item.id, children: arr }];

      if (item.id === targetId) {
        return newPath;
      }

      if (item.children.length > 0) {
        const foundPath = findPathById(item.children, targetId, newPath);
        if (foundPath) {
          return foundPath;
        }
      }
    }

    return null;
  };

  setLayoutSubheader(
    categoryIdActive || selectedCategoryId
      ? {
          // @ts-ignore
          breadcrumb: [
            {
              name: fm('MENU.PRODUCTS.CATALOG'),
            },
            ...(findPathById(companyCategories, categoryIdActive || selectedCategoryId) || []),
          ].map((element: any) => {
            return {
              title:
                element.name?.length > 30 ? `${element.name.slice(0, 30)} ... ` : element.name,
              hoverTitle: element.name,
              root: true,
              id: element.id,
              children: element.children,
              page: `products/catalog/company/${companyId}`,
              onClickCategory: (id: number) => {
                setCategoryIdActive(id);
              },
              click: () => {
                const newCategory = element.id || 0;
                setCategoryIdActive(newCategory);
                if (newCategory === 0) {
                  categoriesRef.current?.resetCategories();
                }
              },
              isActive: element.id === (categoryIdActive || selectedCategoryId),
            };
          }),
        }
      : {
          title: fm('MENU.PRODUCTS.CATALOG'),
        }
  );

  setLayoutFooter({ show: true });

  const resetProducts = useCallback(() => {
    fetch({
      id: companyId,
      data: {
        page: 1,
        per_page: 10,
        category_id: undefined,
      },
    });
  }, []);

  if (loadingMe) {
    return <Preloader />;
  }

  if (me && !me.is_admin && !isManager && !isVendor) {
    navigate('/');
  }

  if (me && !isManager && !me.is_admin && !me.company) {
    return <CompanyBind />;
  }

  if (me && isVendor && me.company && me.company.id !== companyId) {
    navigate(`/products/catalog/company/${me.company.id}`);
  }

  return (
    <>
      <AlertDialog
        open={isAlertOpen}
        message={intl.formatMessage({ id: 'PRODUCT.DELETE.TEXT' })}
        okText={intl.formatMessage({ id: 'CATEGORIES.DELETE.OK' })}
        cancelText={intl.formatMessage({ id: 'CATEGORIES.DELETE.CANCEL' })}
        handleClose={() => {
          clearDel();
          setAlertOpen(false);
        }}
        handleAgree={() => deleteAction()}
      />

      <div className={classes.container}>
        <div className={classes.filterCol}>
          <FilterCategoriesTree
            categories={companyCategories || []}
            resetProducts={resetProducts}
            companyId={companyId}
            searchProducts={({ categoryId }) =>
              fetch({
                id: companyId,
                data: { page: 1, per_page: 10, category_id: categoryId || undefined },
              })
            }
            loading={loading}
            perPage={page.perPage}
            setSearch={setSearch}
            selectedCategoryId={selectedCategoryId}
            setSelectedCategoryId={setSelectedCategoryId}
            isAdmin={me?.is_admin}
            categoriesRef={categoriesRef}
          />
        </div>

        <div className={classes.productsCol}>
          <div
            className={clsx(classes.card, {
              [classes.revers]: isSearching,
            })}
          >
            <div>
              <ProductCatalogHeader
                classes={classes}
                fetch={({ page, perPage }) =>
                  fetch({
                    id: companyId,
                    data: {
                      page,
                      per_page: perPage,
                      category_id: categoryIdActive || selectedCategoryId || undefined,
                    },
                  })
                }
                isAdmin={isAdmin}
                page={page.page}
                perPage={page.perPage}
                products={products || []}
                selectedCategoryId={selectedCategoryId}
                setViewType={setViewType}
                total={page.total}
                viewType={viewType}
              />
              {loading ? (
                <Preloader />
              ) : (
                <>
                  {!products || !products.length ? (
                    <>
                      <Typography className={classes.empty} component='h5' variant='h5'>
                        {intl.formatMessage({ id: 'PRODUCT.CATALOG.LIST.EMPTY' })}
                      </Typography>
                      <Typography className={classes.emptyEmail}>
                        Если нужного товара нет в наличии, то отправьте запрос на почту{' '}
                        <a href='mailto:1@chipscan.ru'>1@chipscan.ru</a> или{' '}
                        <a href='mailto:2@chipscan.ru'>2@chipscan.ru</a>, мы обязательно с вами
                        свяжемся!
                      </Typography>
                    </>
                  ) : (
                    <CompanyContext.Provider value={{ companyId }}>
                      {effectiveViewType === 'table' ? (
                        <ProductsTableListComponent
                          data={products}
                          isAuthorized={isAuthorized}
                          me={me}
                          companyId={companyId}
                        />
                      ) : (
                        <>
                          {effectiveViewType === 'list' ? (
                            <>
                              {isAdmin ? (
                                <ProductsTableListForAdminComponent
                                  data={products}
                                  isAuthorized={isAuthorized}
                                  isAdmin={!!isAdmin}
                                  me={me}
                                  addProductLoading={addProductLoading}
                                  handleCartDialog={handleCartDialog}
                                  editAction={editAction}
                                  handleDeleteDialog={handleDeleteDialog}
                                />
                              ) : (
                                <ProductsTableForUsersComponent
                                  data={products}
                                  isAuthorized={isAuthorized}
                                  me={me}
                                />
                              )}
                            </>
                          ) : (
                            <ProductsGrid me={me} data={products} companyId={companyId} />
                          )}
                        </>
                      )}
                      {!!fetch && (
                        <ProductCatalogHeader
                          classes={classes}
                          fetch={({ page, perPage }) =>
                            fetch({
                              id: companyId,
                              data: {
                                page,
                                per_page: perPage,
                                category_id:
                                  categoryIdActive || selectedCategoryId || undefined,
                              },
                            })
                          }
                          isAdmin={isAdmin}
                          page={page.page}
                          perPage={page.perPage}
                          products={products}
                          selectedCategoryId={selectedCategoryId}
                          setViewType={setViewType}
                          total={page.total}
                          viewType={viewType}
                        />
                      )}
                    </CompanyContext.Provider>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const connector = connect(
  (state: IAppState) => ({
    deleteError: state.productsCatalog.delError,
    viewType: state.productsCatalog.viewType,
    isSearching: state.productsCatalog.isSearching,

    catalogCategories: state.categories.catalogCategories,

    me: state.profile.me,
    loadingMe: state.profile.loading,

    addProductLoading: state.cart.addProductLoading,
    addProductSuccess: state.cart.addProductSuccess,
    addProductError: state.cart.addProductError,
    cartCompanyId: state.cart.companyId,
    isAuthorized: state.auth.user != null,
    companyGuestCart: state.cart.guestCart?.cartCompanyId,
    isAdmin: state.auth.user?.is_admin,
    delSuccess: state.productsCatalog.delSuccess,
    sortType: state.productsCatalog.sortType,
    search: state.productsCatalog.search,
    categoryIdActive: state.productsCatalog.categoryIdActive,
  }),
  {
    delProduct: productCatalogActions.delRequest,
    clearDel: productCatalogActions.clearDel,
    setViewType: productCatalogActions.setViewType,
    setSearch: productCatalogActions.setSearch,
    setCategoryIdActive: productCatalogActions.setCategoryIdActive,

    fetchMe: profileActions.fetchRequest,
    clearMe: profileActions.clearMe,

    addProductToCart: cartActions.addProductRequest,
    setProductGuestCart: cartActions.setProductGuestCart,

    fetchCatalogCategories: categoriesActions.fetchCatalogCategories,

    clearAddProduct: cartActions.clearAddProduct,
  }
);

type TPropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ProductCatalogCompany);
